import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { useToast } from '@/hooks/use-toast';
import { useCategories } from '@/hooks/useCategories';
import { getBusinessStatus } from '@/utils/businessUtils';
import { useBusinessDiscountForManagement } from '@/hooks/useBusinessDiscount';
import { useBusinessSubscriptionStatus } from '@/hooks/useBusinessSubscriptionStatus';
import { WeeklyHours, createDefaultOpeningHours, parseOpeningHours, formatOpeningHours } from '@communitly/shared';
import { useTimeFormatParameters } from '@/hooks/useParameters';
import { PATH } from '@/routes/routeConfig';

// UI Components
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Icons
import { 
  Edit, 
  Trash2, 
  Phone, 
  Mail, 
  MapPin, 
  Globe, 
  AlertTriangle,
  ArrowLeft,
  Save,
  X,
  Image,
  CheckCircle,
  Loader2,
  Instagram,
  Facebook,
  ExternalLink,
  Clock,
  Award,
  Building2,
  ImageIcon,
  Tag
} from 'lucide-react';

// Custom Components
import EditableCard from '@/components/business/EditableCard';
import BusinessHeader from '@/components/business/BusinessHeader';
import BusinessSidebar from '@/components/business/BusinessSidebar';
import { BusinessBadges } from '@/components/BusinessBadges';
import ImageUploader from '@/components/ImageUploader';
import SubscriptionStatus from '@/components/SubscriptionStatus';
import BusinessDiscountSection from '@/components/business/BusinessDiscountSection';
import BusinessDiscountForm from '@/components/business/BusinessDiscountForm';
import BusinessDeletionWarning from '@/components/admin/BusinessDeletionWarning';


const MyBusiness = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userRole } = useCommunity();
  const { refreshBusinessData } = useBusiness();
  const { timeDisplayFormat } = useTimeFormatParameters();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State management
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeletionSuccess, setShowDeletionSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [showListingConfirmation, setShowListingConfirmation] = useState(false);
  
  // Image upload states
  const [uploadingImages, setUploadingImages] = useState({
    banner: false,
    logo: false,
    business1: false,
    business2: false,
    business3: false
  });
  
  // Editing states - only for business overview now
  const [isEditing, setIsEditing] = useState<{
    generalInfo: boolean;
  }>({
    generalInfo: false
  });
  
  // Sidebar editing states
  const [sidebarEditing, setSidebarEditing] = useState<{
    contact: boolean;
    location: boolean;
    openingHours: boolean;
  }>({
    contact: false,
    location: false,
    openingHours: false
  });
  
  const [editedData, setEditedData] = useState({
    name: '',
    description: '',
    category_id: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postal_code: '',
    country: ''
  });

  // Opening hours state
  const [editedOpeningHours, setEditedOpeningHours] = useState<WeeklyHours>(createDefaultOpeningHours());
  
  // Business showcase image editing states
  const [activeBusinessImageTab, setActiveBusinessImageTab] = useState('image1');
  
  const { data: categoriesData } = useCategories();
  
  const getCategoryName = (categoryId: string): string => {
    if (!categoriesData?.categories) return 'Uncategorized';
    const category = categoriesData.categories.find(cat => cat.id === categoryId);
    return category?.name || 'Uncategorized';
  };
  
  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          id, 
          translation_key,
          translation_defaults!inner(key, default_value)
        `)
        .order('translation_key');
        
      if (error) throw error;
      return data;
    }
  });
  
  const { data: business, isLoading, error } = useQuery({
    queryKey: ['myBusiness', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          *,
          categories(id, translation_key)
        `)
        .eq('user_id', user.id)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setEditedData({
          name: data.name || '',
          description: data.description || '',
          category_id: data.category_id || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          website: data.website || '',
          instagram_url: data.instagram_url || '',
          facebook_url: data.facebook_url || '',
          tiktok_url: data.tiktok_url || '',
          address_line1: data.address_line1 || '',
          address_line2: data.address_line2 || '',
          city: data.city || '',
          county: data.county || '',
          postal_code: data.postal_code || '',
          country: data.country || ''
        });

        // Parse opening hours
        const parsedHours = parseOpeningHours(data.opening_hours ? JSON.stringify(data.opening_hours) : null);
        setEditedOpeningHours(parsedHours || createDefaultOpeningHours());
      }
      
      return data;
    },
    enabled: !!user
  });
  
  const { data: currentDiscount } = useBusinessDiscountForManagement(business?.id);
  const { data: subscriptionInfo } = useBusinessSubscriptionStatus(business?.id || '');

  // Listing confirmation logic
  useEffect(() => {
    const confirmListing = searchParams.get('confirm_listing');
    if (confirmListing === 'true' && business?.is_active && !business?.is_listed) {
      setShowListingConfirmation(true);
      // Remove the query parameter from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, business?.is_active, business?.is_listed]);

  const confirmListingMutation = useMutation({
    mutationFn: async () => {
      if (!business?.id) throw new Error('No business found');
      
      const { data, error } = await supabase.functions.invoke('confirm-business-listing', {
        body: { business_id: business.id }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['myBusiness'] });
      refreshBusinessData();
      setShowListingConfirmation(false);
      
      const emailType = data.email_sent === 'business_listed' ? 'confirmation' : 'subscription reminder';
      toast({
        title: "Business Listed Successfully!",
        description: `Your business is now visible to community members. A ${emailType} email has been sent to you.`,
      });
    },
    onError: (error: any) => {
      console.error('Failed to confirm listing:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to confirm business listing',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (sectionData: Partial<typeof editedData> & { opening_hours?: WeeklyHours }) => {
      if (!business) return;
      
      const { error } = await supabase
        .from('businesses')
        .update(sectionData)
        .eq('id', business.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Business updated",
        description: "Your business information has been updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['myBusiness'] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update business: ${error.message}`,
      });
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!business) return;
      
      const { error } = await supabase
        .from('businesses')
        .delete()
        .eq('id', business.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Business deleted",
        description: "Your business has been successfully deleted",
      });
      
      setShowDeletionSuccess(true);
      
      const redirectTo = (userRole === 'admin' || userRole === 'superadmin' || userRole === 'reviewer') 
        ? '/admin' 
        : '/';
      
      const timer = setInterval(() => {
        setCountdown(prevCountdown => {
          if (prevCountdown <= 1) {
            clearInterval(timer);
            navigate(redirectTo);
            
            setTimeout(() => {
              refreshBusinessData();
              queryClient.invalidateQueries({ queryKey: ['myBusiness'] });
            }, 500);
            
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to delete business: ${error.message}`,
      });
    }
  });
  
  const updateImagesMutation = useMutation({
    mutationFn: async (imageData: {
      company_logo?: string | null;
      company_banner_url?: string | null;
      business_image_1_url?: string | null;
      business_image_2_url?: string | null;
      business_image_3_url?: string | null;
    }) => {
      if (!business) return;
      
      const { error } = await supabase
        .from('businesses')
        .update(imageData)
        .eq('id', business.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Images updated",
        description: "Your business images have been updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['myBusiness'] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update images: ${error.message}`,
      });
    }
  });
  
  const handleImageUploadFromFile = async (file: File, imageType: 'banner' | 'logo' | 'business1' | 'business2' | 'business3') => {
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB',
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, JPEG, GIF)',
        variant: 'destructive'
      });
      return;
    }

    setUploadingImages(prev => ({ ...prev, [imageType]: true }));

    try {
      // Get user ID for folder structure
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      const userId = user.id;
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      
      const folderMap = {
        banner: 'banner',
        logo: 'logo',
        business1: 'business',
        business2: 'business',
        business3: 'business'
      };
      
      const filePath = `${userId}/${folderMap[imageType]}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('business_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);

      const fieldMap = {
        banner: 'company_banner_url',
        logo: 'company_logo',
        business1: 'business_image_1_url',
        business2: 'business_image_2_url',
        business3: 'business_image_3_url'
      };

      updateImagesMutation.mutate({ [fieldMap[imageType]]: publicUrl });
      
      toast({
        title: 'Image uploaded',
        description: 'Your image was uploaded successfully',
      });
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'There was a problem uploading your image',
        variant: 'destructive'
      });
    } finally {
      setUploadingImages(prev => ({ ...prev, [imageType]: false }));
    }
  };
  
  const toggleEditing = (section: keyof typeof isEditing) => {
    const wasEditing = isEditing[section];
    const isEnteringEditMode = !wasEditing;
    
    setIsEditing(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    
    if (business) {
      if (isEnteringEditMode) {
        // Populate data when entering edit mode
        setEditedData(prev => ({
          ...prev,
          ...(section === 'generalInfo' && {
            name: business.name || '',
            description: business.description || '',
            category_id: business.category_id || '',
          }),
        }));
      } else {
        // Reset data when canceling edit (wasEditing = true means we're exiting edit mode)
        setEditedData(prev => ({
          ...prev,
          ...(section === 'generalInfo' && {
            name: business.name || '',
            description: business.description || '',
            category_id: business.category_id || '',
          }),
        }));
      }
    }
  };

  // Sidebar editing functions
  const toggleSidebarEditing = (section: keyof typeof sidebarEditing) => {
    const isEnteringEditMode = !sidebarEditing[section];
    
    setSidebarEditing(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    
    if (isEnteringEditMode && business) {
      // Populate data when entering edit mode
      setEditedData(prev => ({
        ...prev,
        ...(section === 'contact' && {
          contact_email: business.contact_email || '',
          contact_phone: business.contact_phone || '',
          website: business.website || '',
          instagram_url: business.instagram_url || '',
          facebook_url: business.facebook_url || '',
          tiktok_url: business.tiktok_url || '',
        }),
        ...(section === 'location' && {
          address_line1: business.address_line1 || '',
          address_line2: business.address_line2 || '',
          city: business.city || '',
          county: business.county || '',
          postal_code: business.postal_code || '',
          country: business.country || '',
        }),
      }));

      if (section === 'openingHours') {
        const parsedHours = parseOpeningHours(business.opening_hours ? JSON.stringify(business.opening_hours) : null);
        setEditedOpeningHours(parsedHours || createDefaultOpeningHours());
      }
    } else if (!isEnteringEditMode && business) {
      // Reset data when canceling edit mode
      setEditedData(prev => ({
        ...prev,
        ...(section === 'contact' && {
          contact_email: business.contact_email || '',
          contact_phone: business.contact_phone || '',
          website: business.website || '',
          instagram_url: business.instagram_url || '',
          facebook_url: business.facebook_url || '',
          tiktok_url: business.tiktok_url || '',
        }),
        ...(section === 'location' && {
          address_line1: business.address_line1 || '',
          address_line2: business.address_line2 || '',
          city: business.city || '',
          county: business.county || '',
          postal_code: business.postal_code || '',
          country: business.country || '',
        }),
      }));

      if (section === 'openingHours') {
        const parsedHours = parseOpeningHours(business.opening_hours ? JSON.stringify(business.opening_hours) : null);
        setEditedOpeningHours(parsedHours || createDefaultOpeningHours());
      }
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const saveChanges = (section: keyof typeof isEditing) => {
    let updateData: Partial<typeof editedData> = {};
    
    if (section === 'generalInfo') {
      updateData = {
        name: editedData.name,
        description: editedData.description,
        category_id: editedData.category_id,
      };
    }
    
    updateMutation.mutate(updateData);
    toggleEditing(section);
  };

  // Sidebar save functions
  const saveSidebarChanges = (section: keyof typeof sidebarEditing, event?: React.FormEvent) => {
    // If it's a form event, prevent default and check validity
    if (event && event.target instanceof HTMLFormElement) {
      event.preventDefault();
      
      // Check if form is valid (HTML5 validation)
      if (!event.target.checkValidity()) {
        // Form validation failed, let browser show validation messages
        return;
      }
    }
    
    let updateData: Partial<typeof editedData> & { opening_hours?: WeeklyHours } = {};
    
    if (section === 'contact') {
      updateData = {
        contact_email: editedData.contact_email,
        contact_phone: editedData.contact_phone,
        website: editedData.website,
        instagram_url: editedData.instagram_url,
        facebook_url: editedData.facebook_url,
        tiktok_url: editedData.tiktok_url,
      };
    } else if (section === 'location') {
      updateData = {
        address_line1: editedData.address_line1,
        address_line2: editedData.address_line2,
        city: editedData.city,
        county: editedData.county,
        postal_code: editedData.postal_code?.toUpperCase() || '',
        country: editedData.country,
      };
    } else if (section === 'openingHours') {
      updateData = {
        opening_hours: editedOpeningHours,
      };
    }
    
    updateMutation.mutate(updateData);
    toggleSidebarEditing(section);
  };
  
  const handleImageUpload = (field: string, url: string) => {
    updateImagesMutation.mutate({ [field]: url });
  };
  
  const handleImageRemove = (field: string) => {
    updateImagesMutation.mutate({ [field]: null });
  };
  
  const handleDeleteBusiness = async () => {
    // Allow deletion during grace period (user forfeits remaining subscription time)
    if (subscriptionInfo?.hasActiveSubscription && !subscriptionInfo?.isInGracePeriod) {
      toast({
        title: "Cannot Delete Business",
        description: "Your business has an active subscription. Please cancel your subscription and wait for it to expire before deleting your business.",
        variant: "destructive",
      });
      return;
    }

    setDeleteConfirmation('');
    deleteMutation.mutate();
  };
  
  const handleEditDiscount = () => {
    setShowDiscountForm(true);
  };

  const handleCreateDiscount = () => {
    setShowDiscountForm(true);
  };

  const handleDiscountFormSuccess = () => {
    setShowDiscountForm(false);
  };

  const handleDiscountFormCancel = () => {
    setShowDiscountForm(false);
  };

  if (isLoading) {
    return (
      <div className="container max-w-7xl mx-auto py-10 px-4 md:px-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container max-w-7xl mx-auto py-6 px-4">
        <Card className="border-destructive">
          <CardHeader>
            <div className="flex items-center">
              <AlertTriangle className="text-destructive mr-2 h-5 w-5" />
              <CardTitle>Error</CardTitle>
            </div>
            <CardDescription>
              We couldn't load your business information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p>{error instanceof Error ? error.message : 'An unknown error occurred'}</p>
          </CardContent>
          <CardFooter>
            <Button variant="outline" onClick={() => navigate('/admin')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Return to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  if (showDeletionSuccess) {
    return (
      <div className="container max-w-7xl mx-auto py-10 px-4 md:px-6">
        <Card className="text-center py-10">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Your Business Has Been Deleted</CardTitle>
            <CardDescription>
              All related data has been successfully removed from our system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-8">
              You will be redirected in <span className="font-bold">{countdown}</span> seconds...
            </p>
            <Button 
              onClick={() => {
                const redirectTo = (userRole === 'admin' || userRole === 'superadmin' || userRole === 'reviewer') 
                  ? '/admin' 
                  : '/';
                
                navigate(redirectTo);
                
                setTimeout(() => {
                  refreshBusinessData();
                }, 500);
              }}
            >
              Continue Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!business) {
    return (
      <div className="container max-w-7xl mx-auto py-6 px-4">
        <Card>
          <CardHeader>
            <CardTitle>No Business Found</CardTitle>
            <CardDescription>
              You don't have a business registered yet.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Would you like to register a new business?
            </p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/services')}>
              View Services
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">My Business</h1>
        <p className="text-muted-foreground/80 text-lg">
          Manage your business profile, subscription, and settings
        </p>
      </div>
        {/* Business Header with WYSIWYG editing */}
        <BusinessHeader 
          business={business} 
          onViewPublicPage={() => navigate(PATH.SERVICE_VIEW.replace(':slug', business.slug))}
          editMode={true}
          onBannerUpload={(file) => handleImageUploadFromFile(file, 'banner')}
          onLogoUpload={(file) => handleImageUploadFromFile(file, 'logo')}
          uploading={{
            banner: uploadingImages.banner,
            logo: uploadingImages.logo
          }}
          onShowListingConfirmation={() => setShowListingConfirmation(true)}
        />

        {/* Subscription Status */}
        {business?.id && business.is_active && (
          <div className="mb-8">
            <SubscriptionStatus 
              businessId={business.id}
              showTitle={true}
              isActive={business.is_active}
              discountType={currentDiscount?.type}
            />
          </div>
        )}

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
          {/* Left Column - Main Content (Desktop: 2/3 width) */}
          <div className="lg:col-span-2 space-y-4 lg:space-y-6 min-w-0">
            {/* Business Overview */}
            <EditableCard
              title="Business Overview"
              description="Basic information about your business"
              isEditing={isEditing.generalInfo}
              onToggleEdit={() => toggleEditing('generalInfo')}
              onSave={() => saveChanges('generalInfo')}
              icon={Building2}
              iconColor="text-blue-600"
            >
              {isEditing.generalInfo ? (
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-medium">Business Name</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      value={editedData.name} 
                      onChange={handleInputChange} 
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="category" className="text-base font-medium">Category</Label>
                    <Select 
                      value={editedData.category_id} 
                      onValueChange={(value) => handleSelectChange('category_id', value)}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories && !categoriesError ? (
                          categories.map((category) => (
                            <SelectItem key={category?.id || 'default'} value={category?.id || 'default'}>
                              {category?.translation_defaults?.default_value || 'Unknown Category'}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="default">Loading categories...</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="description" className="text-base font-medium">Description</Label>
                    <Textarea 
                      id="description"
                      name="description" 
                      value={editedData.description} 
                      onChange={handleInputChange} 
                      className="mt-2 min-h-32"
                      placeholder="Describe your business, services, and what makes you unique..."
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-6">
                  {/* Business Name Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Business Name</label>
                    <h3 className="text-lg sm:text-xl font-semibold text-foreground break-words">
                      {business.name}
                    </h3>
                  </div>

                  {/* Category Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Category</label>
                    <div className="flex items-center">
                      <Badge variant="secondary" className="bg-muted text-foreground truncate max-w-full">
                        {getCategoryName(business.category_id)}
                      </Badge>
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed break-words">
                      {business.description || "No description provided."}
                    </p>
                  </div>
                </div>
              )}
            </EditableCard>

            {/* Business Achievements */}
            <EditableCard
              title="Business Achievements"
              description="Recognition badges your business has earned"
              isEditing={false}
              onToggleEdit={() => {}}
              showEditButton={false}
              icon={Award}
              iconColor="text-yellow-600"
            >
              <BusinessBadges 
                businessId={business.id}
                mode="management"
                className="mt-2"
              />
            </EditableCard>

            {/* Business Showcase - keeping this section for the 3 business images */}
            <EditableCard
              title="Business Showcase"
              description="Images that showcase your products, services, or business space"
              isEditing={false}
              onToggleEdit={() => {}}
              showEditButton={false}
              icon={ImageIcon}
              iconColor="text-purple-600"
            >
              <Tabs 
                defaultValue="image1" 
                value={activeBusinessImageTab} 
                onValueChange={setActiveBusinessImageTab}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-3 text-xs sm:text-sm">
                  <TabsTrigger value="image1" className="truncate px-2 sm:px-3">Image 1</TabsTrigger>
                  <TabsTrigger value="image2" className="truncate px-2 sm:px-3">Image 2</TabsTrigger>
                  <TabsTrigger value="image3" className="truncate px-2 sm:px-3">Image 3</TabsTrigger>
                </TabsList>
                
                <TabsContent value="image1" className="mt-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Business Image 1</Label>
                      <p className="text-sm text-muted-foreground mt-1 mb-4">
                        Showcase your products, services, or business space to attract customers.
                      </p>
                    </div>
                    <div className="max-w-md mx-auto px-2 sm:px-4 lg:px-0">
                      <ImageUploader
                        imageUrl={business?.business_image_1_url || null}
                        onImageUploaded={(url) => handleImageUpload('business_image_1_url', url)}
                        onImageRemoved={() => handleImageRemove('business_image_1_url')}
                        folder="business"
                        aspectRatio="square"
                        buttonText="Upload Business Image 1"
                        editable={true}
                        editMode={true}
                      />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="image2" className="mt-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Business Image 2</Label>
                      <p className="text-sm text-muted-foreground mt-1 mb-4">
                        Add another image to highlight different aspects of your offerings.
                      </p>
                    </div>
                    <div className="max-w-md mx-auto px-2 sm:px-4 lg:px-0">
                      <ImageUploader
                        imageUrl={business?.business_image_2_url || null}
                        onImageUploaded={(url) => handleImageUpload('business_image_2_url', url)}
                        onImageRemoved={() => handleImageRemove('business_image_2_url')}
                        folder="business"
                        aspectRatio="square"
                        buttonText="Upload Business Image 2"
                        editable={true}
                        editMode={true}
                      />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="image3" className="mt-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Business Image 3</Label>
                      <p className="text-sm text-muted-foreground mt-1 mb-4">
                        Add a third image to create a comprehensive visual gallery for customers.
                      </p>
                    </div>
                    <div className="max-w-md mx-auto px-2 sm:px-4 lg:px-0">
                      <ImageUploader
                        imageUrl={business?.business_image_3_url || null}
                        onImageUploaded={(url) => handleImageUpload('business_image_3_url', url)}
                        onImageRemoved={() => handleImageRemove('business_image_3_url')}
                        folder="business"
                        aspectRatio="square"
                        buttonText="Upload Business Image 3"
                        editable={true}
                        editMode={true}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </EditableCard>

            {/* Business Discount Section */}
            <BusinessDiscountSection
              businessId={business.id}
              onEditDiscount={handleEditDiscount}
              onCreateDiscount={handleCreateDiscount}
            />
          </div>

          {/* Right Column - Sidebar (Desktop: 1/3 width) */}
          <div className="lg:col-span-1 min-w-0">
            <BusinessSidebar 
              business={business}
              editingStates={sidebarEditing}
              onToggleEdit={toggleSidebarEditing}
              onSave={saveSidebarChanges}
              editedData={editedData}
              onInputChange={handleInputChange}
              editedOpeningHours={editedOpeningHours}
              onOpeningHoursChange={setEditedOpeningHours}
              timeDisplayFormat={timeDisplayFormat}
            />
          </div>
        </div>

        {/* Danger Zone - Placed last to ensure it appears at the bottom on all screen sizes */}
        <div className="mt-6 lg:mt-8">
          <Card className="border-destructive/50 bg-red-50 shadow-sm">
            <CardHeader>
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Danger Zone
                </CardTitle>
                <CardDescription>Irreversible actions for your business</CardDescription>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {business?.id && subscriptionInfo?.hasActiveSubscription && (
                <BusinessDeletionWarning businessId={business.id} />
              )}
              
              <p className="text-muted-foreground text-sm">
                Deleting your business will permanently remove all information, reviews, and discounts associated with it. This action cannot be undone.
                {subscriptionInfo?.hasActiveSubscription && !subscriptionInfo?.isInGracePeriod && (
                  <span className="block mt-2 text-destructive font-medium break-words">
                    Note: You cannot delete your business while you have an active subscription. Please cancel your subscription and wait for it to expire first.
                  </span>
                )}
                {subscriptionInfo?.isInGracePeriod && (
                  <span className="block mt-2 text-amber-600 font-medium break-words">
                    Note: Deleting your business during the grace period will forfeit any remaining subscription benefits. No refunds will be provided.
                  </span>
                )}
              </p>
              
              <AlertDialog onOpenChange={() => setDeleteConfirmation('')}>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    disabled={subscriptionInfo?.hasActiveSubscription && !subscriptionInfo?.isInGracePeriod}
                    className={subscriptionInfo?.hasActiveSubscription && !subscriptionInfo?.isInGracePeriod ? 'opacity-50 cursor-not-allowed' : ''}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Business
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm Business Deletion</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-4">
                      <p>
                        This will permanently delete your business listing and all associated data. This action cannot be undone.
                      </p>
                      
                      {subscriptionInfo?.isInGracePeriod && (
                        <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                          <p className="font-medium text-amber-800">
                            ⚠️ Grace Period Warning
                          </p>
                          <p className="text-sm text-amber-700 mt-1">
                            You are currently in a grace period. By deleting your business now, you will forfeit any remaining subscription benefits until {subscriptionInfo.subscriptionEndDate && new Date(subscriptionInfo.subscriptionEndDate).toLocaleDateString()}. No refunds will be provided.
                          </p>
                        </div>
                      )}
                      
                      <div className="bg-muted p-3 rounded-md my-3">
                        <p className="font-medium">To confirm deletion, please type the business name:</p>
                        <p className="font-bold mt-1 break-words">{business.name}</p>
                      </div>
                      <div className="pt-2">
                        <Label htmlFor="confirm-name">Business name confirmation</Label>
                        <Input
                          id="confirm-name"
                          value={deleteConfirmation}
                          onChange={(e) => setDeleteConfirmation(e.target.value)}
                          placeholder="Type business name here"
                          className="mt-1 w-full"
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDeleteBusiness}
                      disabled={deleteConfirmation !== business.name}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                    >
                      {subscriptionInfo?.isInGracePeriod ? 'Delete & Forfeit Benefits' : 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>

        {/* Listing Confirmation Modal */}
        <Dialog open={showListingConfirmation} onOpenChange={setShowListingConfirmation}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">🎉 Confirm Your Business Listing</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Great news! Your business <strong>{business?.name}</strong> has been approved. 
                Are you ready to make it visible to community members?
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">✅ What happens next:</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Your business becomes visible in the community directory</li>
                  <li>• Community members can discover and contact your business</li>
                  <li>• You can start receiving customer inquiries</li>
                </ul>
              </div>
              
              {business?.business_discounts?.[0]?.type === 'Paid' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 mb-2">📋 Subscription Note:</h4>
                  <p className="text-sm text-amber-700">
                    Your business will need an active subscription to unlock all features. 
                    You can choose your plan after confirming your listing.
                  </p>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowListingConfirmation(false)}
                  className="flex-1"
                >
                  Not Yet
                </Button>
                <Button 
                  onClick={() => confirmListingMutation.mutate()}
                  disabled={confirmListingMutation.isPending}
                  className="flex-1"
                >
                  {confirmListingMutation.isPending ? 'Confirming...' : 'Confirm Listing'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Discount Form Modal */}
        <Dialog open={showDiscountForm} onOpenChange={setShowDiscountForm}>
          <DialogContent className="max-w-2xl w-[95vw] sm:w-full mx-4 sm:mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>
                {currentDiscount ? 'Create New' : 'Create'} Community Discount
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <BusinessDiscountForm
                businessId={business.id}
                onSuccess={handleDiscountFormSuccess}
                onCancel={handleDiscountFormCancel}
              />
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
};

export default MyBusiness;
